import { createContext, useCallback, useContext, useRef } from 'react';

// A section's save fn; passing null unregisters it.
export type RegisterSave = (id: string, save: (() => Promise<void>) | null) => void;

export const RegisterSaveContext = createContext<RegisterSave | null>(null);

export const useRegisterSave = (): RegisterSave => {
  const registerSave = useContext(RegisterSaveContext);
  if (!registerSave) {
    throw new Error('useRegisterSave must be used within a SettingsPage');
  }
  return registerSave;
};

export const useSectionSavers = () => {
  const saversRef = useRef(new Map<string, () => Promise<void>>());

  const registerSave = useCallback<RegisterSave>((id, save) => {
    if (save) {
      saversRef.current.set(id, save);
    } else {
      saversRef.current.delete(id);
    }
  }, []);

  const saveAllDirty = useCallback(async () => {
    const results = await Promise.allSettled(
      Array.from(saversRef.current.values(), (save) => save())
    );
    const firstRejection = results.find((r) => r.status === 'rejected');
    if (firstRejection) {
      throw (firstRejection as PromiseRejectedResult).reason;
    }
  }, []);

  return { registerSave, saveAllDirty };
};
